// SPDX-License-Identifier: AGPL-3.0-or-later
import { normalizeDescription } from "./parse.js";
/**
 * Picks a category. A correction the user made for the same payee always
 * wins, then the longest matching rule, otherwise it goes to the review queue.
 */
export function categorize(description, amount, rules, learned) {
    const key = payeeKey(description);
    const remembered = learned.get(key);
    if (remembered)
        return { category: remembered, source: "learned" };
    const text = normalizeDescription(description);
    const matches = rules
        .filter((r) => text.includes(r.contains.toUpperCase()))
        .filter((r) => !r.direction || (r.direction === "out" ? amount < 0 : amount > 0))
        .sort((a, b) => b.contains.length - a.contains.length);
    const rule = matches[0];
    return rule ? { category: rule.category, source: "rule" } : { category: null, source: "none" };
}
/**
 * A payee key that ignores store numbers, card digits and dates, so
 * "TIM HORTONS #1234" and "TIM HORTONS #0567" learn together.
 */
export function payeeKey(description) {
    return normalizeDescription(description)
        .replace(/#\s*\d+/g, "")
        .replace(/\b\d{3,}\b/g, "")
        .replace(/\b\d{1,2}\/\d{1,2}(\/\d{2,4})?\b/g, "")
        .replace(/[^A-Z&' ]/g, " ")
        .replace(/\s+/g, " ")
        .trim();
}
/** A starter set of rules for common Canadian payees. Users edit and add to these. Categories are paths such as "housing/utilities/gas". */
export const STARTER_RULES = [
    { id: "r-groceries-1", contains: "LOBLAWS", category: "groceries", direction: "out" },
    { id: "r-groceries-2", contains: "SOBEYS", category: "groceries", direction: "out" },
    { id: "r-groceries-3", contains: "METRO", category: "groceries", direction: "out" },
    { id: "r-groceries-4", contains: "NO FRILLS", category: "groceries", direction: "out" },
    { id: "r-groceries-5", contains: "COSTCO", category: "groceries", direction: "out" },
    { id: "r-groceries-6", contains: "SUPERSTORE", category: "groceries", direction: "out" },
    { id: "r-dining-1", contains: "TIM HORTONS", category: "dining/coffee", direction: "out" },
    { id: "r-dining-2", contains: "STARBUCKS", category: "dining/coffee", direction: "out" },
    { id: "r-dining-3", contains: "UBER EATS", category: "dining/takeout", direction: "out" },
    { id: "r-dining-4", contains: "SKIP", category: "dining/takeout", direction: "out" },
    { id: "r-transport-1", contains: "PETRO-CANADA", category: "transport/fuel", direction: "out" },
    { id: "r-transport-2", contains: "ESSO", category: "transport/fuel", direction: "out" },
    { id: "r-transport-3", contains: "SHELL", category: "transport/fuel", direction: "out" },
    { id: "r-transport-4", contains: "PRESTO", category: "transport/transit", direction: "out" },
    { id: "r-utilities-1", contains: "HYDRO", category: "housing/utilities/electricity", direction: "out" },
    { id: "r-utilities-2", contains: "ENBRIDGE", category: "housing/utilities/gas", direction: "out" },
    { id: "r-phone-1", contains: "ROGERS", category: "housing/utilities/phone", direction: "out" },
    { id: "r-phone-2", contains: "BELL CANADA", category: "housing/utilities/phone", direction: "out" },
    { id: "r-phone-3", contains: "TELUS", category: "housing/utilities/phone", direction: "out" },
    { id: "r-shopping-1", contains: "AMAZON", category: "shopping", direction: "out" },
    { id: "r-shopping-2", contains: "CANADIAN TIRE", category: "shopping", direction: "out" },
    { id: "r-income-1", contains: "PAYROLL", category: "income", direction: "in" },
    { id: "r-income-2", contains: "PAY DEPOSIT", category: "income", direction: "in" },
    { id: "r-etransfer-in", contains: "E-TRANSFER", category: "transfers-in", direction: "in" },
];
//# sourceMappingURL=categorize.js.map